// frontend/src/pages/ExploreCirclesPreview.js

import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDown,
  CalendarBlank,
  CheckCircle,
  Fire,
  MagnifyingGlass,
  MapPin,
  Target,
  Plus,
  UsersThree,
  Handshake,
  CalendarCheck,
  Heart,
} from "phosphor-react";

import logoRings from "../assets/third-place-logo4.png";
import ExploreCircleDetailsPreview from "./ExploreCircleDetailsPreview";
import PreLoginHeader from "../components/PreLoginHeader";
import ProfileCardPreview from "../components/ProfileCardPreview";

import {
  PREVIEW_USER,
  PREVIEW_SHOUTOUTS,
} from "../data/previewData";

/* -----------------------------------------------------------
   DEMO CIRCLES
------------------------------------------------------------ */

const DEMO_CIRCLES = [
  {
    id: 8104,
    name: "Home Cooks",
    description:
      "Weeknight experiments, family recipes, and meals worth making again.",
    members: 196,
    location: "Anywhere",
    category: "Food",
    image_url: "/images/homecookscatpropic1.png",
    member_images: [
      "/images/reevespropic1.jpg",
      "/images/yunapropic1.jpg",
      "/images/jadepropic1.jpg",
    ],
  },
  {
    id: 8102,
    name: "Joyful Art Cafe",
    description:
      "An art café and club for casual creatives. Come join us for painting, pottery, and collage, plus guided workshops and art supplies.",
    members: 184,
    location: "Bowery, New York",
    category: "Creative",
    image_url:
      "https://images.unsplash.com/photo-1541961017774-22349e4a1262?auto=format&fit=crop&w=900&q=85",
    member_images: [
      "/images/aaliyahpropic1.jpg",
      "/images/mayapropic2.png",
      "/images/reevespropic1.jpg",
    ],
  },
  {
    id: 8101,
    name: "NYC Beginner Guitar",
    description:
      "Learn chords, share resources, find practice partners, and sign up for beginner-friendly group lessons together.",
    members: 248,
    location: "New York City",
    category: "Music",
    image_url: "/images/guitarcircle1.png",
    member_images: [
      "/images/yunapropic1.jpg",
      "/images/dhruvpropic1.jpg",
      "/images/jadepropic1.jpg",
    ],
  },
  {
    id: 8103,
    name: "Solidform",
    description:
      "Class milestones, form tips, accountability, and post-workout encouragement.",
    members: 312,
    location: "United States",
    category: "Fitness",
    image_url: "/images/solidcorecircle1.png",
    member_images: [
      "/images/mayapropic2.png",
      "/images/jadepropic1.jpg",
      "/images/aaliyahpropic1.jpg",
    ],
  },
  {
    id: 8105,
    name: "Beginner Pottery",
    description:
      "Share imperfect first pieces, studio recommendations, and small wins at the wheel.",
    members: 143,
    location: "Brooklyn, New York",
    category: "Creative",
    image_url: "/images/matchabowl1.png",
    member_images: [
      "/images/aaliyahpropic1.jpg",
      "/images/jadepropic1.jpg",
      "/images/mayapropic2.png",
    ],
  },
  {
    id: 8106,
    name: "Indie Builders",
    description:
      "For people turning ideas into real products.",
    members: 227,
    location: "Anywhere",
    category: "Building",
    image_url:
      "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=900&q=85",
    member_images: [
      "/images/dhruvpropic1.jpg",
      "/images/reevespropic1.jpg",
      "/images/yunapropic1.jpg",
    ],
  },
  {
    id: 8107,
    name: "Sip & Yap",
    description:
      "The group chat, but IRL.",
    members: 89,
    location: "New York City",
    category: "Social",
    private: true,
    image_url: "/images/wine2.jpg",
    member_images: [
      "/images/jadepropic1.jpg",
      "/images/mayapropic2.png",
      "/images/yunapropic1.jpg",
    ],
  },
  {
    id: 8108,
    name: "NYC Tennis Club",
    description:
      "Conquer the impossible of playing tennis in NYC together. All levels welcome.",
    members: 276,
    location: "New York City",
    category: "Sports",
    image_url:
      "https://images.unsplash.com/photo-1554068865-24cecd4e34b8?auto=format&fit=crop&w=900&q=85",
    member_images: [
      "/images/mayapropic2.png",
      "/images/dhruvpropic1.jpg",
      "/images/aaliyahpropic1.jpg",
    ],
  },
];

const CATEGORIES = [
  "All",
  "Fitness",
  "Creative",
  "Music",
  "Food",
  "Building",
  "Social",
];

/* -----------------------------------------------------------
   LEFT RAIL
------------------------------------------------------------ */

const DemoPlannerPanel = () => {
  const items = [
    {
      icon: CalendarBlank,
      label: "Monthly Calendar",
      value: "12 days",
    },
    {
      icon: Fire,
      label: "Current Streak",
      value: "6 days",
    },
    {
      icon: Target,
      label: "July Goals",
      value: "3 of 5",
    },
    {
      icon: CheckCircle,
      label: "Weekly Plan",
      value: "On track",
    },
  ];

  return (
    <section className="mt-4 rounded-2xl bg-white p-5">
      <p className="mb-1 px-1 sm:text-[16px] tracking-[-0.02em] font-black text-blue-600">
        Your Progress
      </p>

      <div className="space-y-1">
        {items.map(({ icon: Icon, label, value }) => (
          <div
            key={label}
            className="flex items-center justify-between rounded-xl px-2 py-2.5 font-semibold"
          >
            <div className="flex min-w-0 items-center gap-2">
              <Icon
                size={17}
                weight="duotone"
                className="shrink-0 text-blue-600"
              />

              <span className="truncate text-sm text-gray-700">{label}</span>
            </div>

            <span className="ml-2 shrink-0 text-sm font-medium text-gray-500">
              {value}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

/* -----------------------------------------------------------
   CIRCLE CARD
------------------------------------------------------------ */

const CircleCard = ({
  circle,
  onAction,
  isFeaturedPreview = false,
}) => {
  return (
    <article
      className="
        group overflow-hidden rounded-3xl bg-white
        transition duration-200
        hover:-translate-y-0.5 hover:shadow-sm
      "
    >
      <button
        type="button"
        onClick={onAction}
        className="block w-full text-left"
        aria-label={
          isFeaturedPreview
            ? `Preview ${circle.name}`
            : `View ${circle.name}`
        }
      >
        <div className="relative h-44 overflow-hidden bg-blue-100">
          <img
            src={circle.image_url}
            alt={circle.name}
            className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.025]"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

          {circle.private && (
            <span className="absolute right-3 top-3 rounded-full bg-blue-100 px-3 py-1 text-xs font-bold text-blue-700">
              Private
            </span>
          )}

          {isFeaturedPreview && (
            <div className="absolute inset-x-0 top-3 z-10 flex justify-center px-3">
              <span
                className="
                  inline-flex animate-bounce items-center gap-1.5
                  rounded-full bg-blue-600
                  px-3 py-1.5
                  text-xs font-bold text-white
                  shadow-md
                "
              >
                Preview this circle
                <ArrowDown size={14} weight="bold" />
              </span>
            </div>
          )}

          <span className="absolute bottom-3 left-3 rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-blue-700">
            {circle.category}
          </span>
        </div>

        <div className="pb-4 pt-3 px-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="truncate text-[18px] font-black tracking-[-0.025em] text-gray-900">
                {circle.name}
              </h2>

              <div className="mt-1 flex items-center gap-1 text-xs font-medium text-gray-500">
                <MapPin
                  size={13}
                  weight="fill"
                  className="shrink-0 text-blue-500"
                />

                <span className="truncate">{circle.location}</span>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700">
              <UsersThree size={14} weight="duotone" />
              {circle.members}
            </div>
          </div>

          <p className="mt-3 line-clamp-2 min-h-[40px] text-sm leading-snug text-gray-600">
            {circle.description}
          </p>

          <div className="mt-1 flex items-center justify-between pt-4">
            <div className="flex -space-x-2">
              {circle.member_images.map((src, index) => (
                <img
                  key={`${circle.id}-${src}`}
                  src={src}
                  alt=""
                  className="h-8 w-8 rounded-full object-cover ring-2 ring-white"
                  style={{
                    zIndex: circle.member_images.length - index,
                  }}
                />
              ))}

              <div className="grid h-8 min-w-8 place-items-center bg-transparent pl-3 text-xs font-semibold text-blue-700">
                +
                {Math.max(
                  0,
                  circle.members - circle.member_images.length
                )}
              </div>
            </div>
          </div>
        </div>
      </button>
    </article>
  );
};

/* -----------------------------------------------------------
   RIGHT RAIL
------------------------------------------------------------ */

const DemoDiscoverPanel = ({ onAction }) => {
  const popularCircles = DEMO_CIRCLES.slice(0, 4);
  const benefits = [
    {
      icon: Handshake,
      text: "Share progress with people who get it",
    },
    {
      icon: CalendarCheck,
      text: "Find events, accountability & recommendations",
    },
    {
      icon: Heart,
      text: "Build a community around what you love",
    },
  ];

  return (
    <section className="rounded-2xl bg-white p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-[16px] font-black tracking-[-0.02em] text-blue-600">
          <img
            src={logoRings}
            alt=""
            className="h-4 w-4 object-contain"
            style={{
              filter:
                "brightness(0) saturate(100%) invert(28%) sepia(96%) saturate(2785%) hue-rotate(204deg) brightness(97%) contrast(94%)",
            }}
          />
          Popular Circles
        </h2>
      </div>

      <p className="text-sm leading-snug text-gray-600">
        Join circles around the things you already love — or the things you've
        always wanted to try
      </p>

      <div className="mt-3 space-y-2">
        {popularCircles.map((circle) => (
          <div key={circle.id} className="flex items-center gap-3">
            <img
              src={circle.image_url}
              alt={circle.name}
              className="h-11 w-11 shrink-0 rounded-full object-cover"
            />

            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-gray-800">
                {circle.name}
              </p>

              <p className="truncate text-xs text-gray-500">
                {circle.members} members
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 rounded-2xl bg-blue-50 p-2">
        <div className="space-y-1">
          {benefits.map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-start gap-3 rounded-xl px-2 py-1">
              <Icon
                size={18}
                weight="duotone"
                className="mt-0.5 shrink-0 text-blue-600"
              />

              <p className="text-sm leading-snug text-gray-700">
                {text}
              </p>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={onAction}
        className="mt-3 w-full rounded-full bg-blue-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-blue-700"
      >
        Let's get started!
      </button>
    </section>
  );
};

/* -----------------------------------------------------------
   EXPLORE CIRCLES PREVIEW
------------------------------------------------------------ */

function ExploreCirclesPreview({ embedded = false }) {
  const navigate = useNavigate();
  const circleDetailsRef = useRef(null);

  const leftRailRef = useRef(null);
  const rightRailRef = useRef(null);

  const [railHeight, setRailHeight] =
    useState(null);

  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  React.useEffect(() => {
    const updateRailHeight = () => {
      if (window.innerWidth < 900) {
        setRailHeight(null);
        return;
      }

      const leftHeight =
        leftRailRef.current?.scrollHeight || 0;

      const rightHeight =
        rightRailRef.current?.scrollHeight || 0;

      setRailHeight(
        Math.max(leftHeight, rightHeight)
      );
    };

    updateRailHeight();

    const observer = new ResizeObserver(
      updateRailHeight
    );

    if (leftRailRef.current) {
      observer.observe(leftRailRef.current);
    }

    if (rightRailRef.current) {
      observer.observe(rightRailRef.current);
    }

    window.addEventListener(
      "resize",
      updateRailHeight
    );

    return () => {
      observer.disconnect();

      window.removeEventListener(
        "resize",
        updateRailHeight
      );
    };
  }, []);

  const handlePreviewAction = () => {
    navigate("/signup");
  };

  const handleHomeCooksPreview = () => {
    const y =
      circleDetailsRef.current.getBoundingClientRect().top +
      window.pageYOffset +
      2; // tweak this

    window.scrollTo({
      top: y,
      behavior: "smooth",
    });
  };

  const filteredCircles = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return DEMO_CIRCLES.filter((circle) => {
      const categoryMatches =
        category === "All" || circle.category === category;

      const queryMatches =
        !normalizedQuery ||
        circle.name.toLowerCase().includes(normalizedQuery) ||
        circle.description.toLowerCase().includes(normalizedQuery) ||
        circle.location.toLowerCase().includes(normalizedQuery) ||
        circle.category.toLowerCase().includes(normalizedQuery);

      return categoryMatches && queryMatches;
    });
  }, [category, query]);

  return (
    <div className="min-h-screen bg-blue-50 text-[#181411]">
      {!embedded && <PreLoginHeader />}

      {/* Hero */}
      {!embedded && (
        <section className="px-6 pb-4 pt-[4.45rem] text-center md:px-10 lg:pb-8 lg:pt-24">
          <h1 className="mx-auto max-w-3xl text-2xl font-black leading-[0.95] tracking-[-0.03em] text-gray-900 lg:text-[38px]">
            Meet your next{" "}
            <span className="whitespace-nowrap text-blue-500">
              third place
            </span>
          </h1>

          <p className="mx-auto mt-4 max-w-4xl text-sm leading-tight sm:tracking-[-0.02em] text-gray-600 sm:text-[20px]">
            Discover communities built around the things you love,
            <br className="hidden sm:block" />
            {" "} the goals you're chasing,
            <br className="sm:hidden" />
            {" "}and the people you'll grow with
          </p>
        </section>
      )}

      {/* Application preview */}
      <main className="mx-auto min-h-[calc(100vh-220px)] max-w-7xl px-4 pb-8 md:px-10 xl:px-16">
        <div className="grid min-h-[calc(100vh-260px)] grid-cols-1 gap-8 min-[900px]:grid-cols-[1fr_minmax(0,2.15fr)_1fr]">
          {/* LEFT */}
          <aside
            ref={leftRailRef}
            className="
              hidden
              min-[900px]:sticky
              min-[900px]:top-4
              min-[900px]:block
              min-[900px]:h-max
              min-[900px]:self-start
            "
          >
            <ProfileCardPreview
              user={PREVIEW_USER}
              shoutouts={PREVIEW_SHOUTOUTS}
              onPreviewAction={handlePreviewAction}
            />

            <DemoPlannerPanel />
          </aside>

          {/* MIDDLE */}
          <section
            className="
              min-w-0
              sm:pr-0 sm:pl-0 sm:pb-10
              self-stretch
              rounded-scroll

              min-[900px]:overflow-y-auto
            "
            style={{
              height:
                railHeight != null
                  ? `${railHeight}px`
                  : undefined,
            }}
          >
            {/* Search and category filters */}
            <div className="rounded-3xl bg-white p-4 md:p-5">
              <div className="relative">
                <MagnifyingGlass
                  size={18}
                  weight="bold"
                  className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <input
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search circles, interests, or places"
                  className="w-full rounded-full border-0 bg-blue-50 !min-h-0 !min-w-0 py-2 pl-11 pr-4 text-sm text-gray-900 outline-none ring-1 ring-transparent transition placeholder:text-gray-400 focus:ring-blue-300"
                />
              </div>

              <div className="mt-4 flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                {CATEGORIES.map((item) => {
                  const active = category === item;

                  return (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setCategory(item)}
                      className={`shrink-0 rounded-full !min-h-0 !min-w-0 px-4 py-1.5 text-sm font-semibold transition ${
                        active
                          ? "bg-blue-600 text-white"
                          : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                      }`}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Section heading */}
            <div className="mb-4 mt-3 ml-1 flex items-center justify-between px-1">
              <div>
                <h2 className="text-lg font-black tracking-[-0.02em] text-gray-900">
                  Explore Circles
                </h2>

                <p className="text-sm text-gray-500">
                  {filteredCircles.length}{" "}
                  communit
                  {filteredCircles.length === 1 ? "y" : "ies"} to discover
                </p>
              </div>
            </div>

            {/* Circle cards */}
            {filteredCircles.length > 0 ? (
              <div className="grid grid-cols-1 px-1 gap-5 min-[990px]:grid-cols-2">
                {filteredCircles.map((circle) => {
                  const isHomeCooks = circle.id === 8104;

                  return (
                    <CircleCard
                      key={circle.id}
                      circle={circle}
                      isFeaturedPreview={isHomeCooks}
                      onAction={
                        isHomeCooks
                          ? handleHomeCooksPreview
                          : handlePreviewAction
                      }
                    />
                  );
                })}
              </div>
            ) : (
              <div className="rounded-3xl bg-white px-6 py-14 text-center">
                <UsersThree
                  size={34}
                  weight="duotone"
                  className="mx-auto text-blue-500"
                />

                <h3 className="mt-3 font-black text-gray-900">
                  No circles found
                </h3>

                <p className="mt-1 text-sm text-gray-500">
                  Try another search or category
                </p>
              </div>
            )}
          </section>

          {/* RIGHT */}
          <aside className="hidden self-stretch min-[900px]:block">
            <div
              ref={rightRailRef}
              className="sticky top-4 h-max space-y-4"
            >
              <button
                type="button"
                onClick={handlePreviewAction}
                className="w-full rounded-full bg-blue-600 !min-h-0 !min-w-0 !px-4 !py-2.5 text-[16px] font-semibold text-white transition hover:bg-blue-700"
              >
                Create a circle
              </button>

              <DemoDiscoverPanel onAction={handlePreviewAction} />
            </div>
          </aside>
        </div>
      </main>

      <section
        id="home-cooks-details-preview"
        ref={circleDetailsRef}
        className={`scroll-mt-20 mt-3 bg-blue-50 pt-4 sm:pt-8 ${
          embedded ? "" : ""
        }`}
      >
        <div className="px-6 sm:pb-8 pb-6 text-center md:px-10">
          <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-blue-700">
            Circle preview
          </span>

          <h2 className="mx-auto mt-3 max-w-3xl text-2xl font-black leading-[0.95] tracking-[-0.03em] text-gray-900 sm:text-3xl lg:text-[38px]">
            Your {" "}
            <span className="whitespace-nowrap text-blue-500">
              third place
            </span>
            {" "} starts here
          </h2>

          <p className="mx-auto mt-4 max-w-4xl text-sm leading-tight tracking-[-0.02em] text-gray-600 sm:text-[20px]">
            Share progress, organize online and offline events,
            <br className="sm:hidden" />
            <br className="hidden sm:block" />
            {" "}celebrate every step,
            <br className="sm:hidden" />
            {" "}and turn hobbies into real community
          </p>
        </div>
        <ExploreCircleDetailsPreview embedded />
      </section>
    </div>
  );
}

export default ExploreCirclesPreview;